/**
 * 图片校验工具类
 */

export enum ImageInvalid {
  FILE_TYPE = 'FILE_TYPE',
  FILE_SIZE = 'FILE_SIZE',
  RESOLUTION = 'RESOLUTION',
  UNKNOWN = 'UNKNOWN'
}

export interface ValidationResult {
  isValid: boolean;
  errorType?: ImageInvalid;
  errorMessage?: string;
}

export interface ImageValidationOptions {
  minWidth?: number;    // 最小宽度（像素）
  minHeight?: number;   // 最小高度（像素）
  maxWidth?: number;    // 最大宽度（像素）
  maxHeight?: number;   // 最大高度（像素）
  maxFileSize?: number; // 最大文件大小（字节）
  minFileSize?: number; // 最小文件大小（字节）
  allowedTypes?: string[]; // 允许的文件类型（MIME类型）
}

export interface ImageMetadata {
  width: number;
  height: number;
  fileSize: number;
  fileType: string;
}

/**
 * 获取图片元数据
 * @param file 图片文件
 * @returns Promise<ImageMetadata>
 */
export function getImageMetadata(file: File): Promise<ImageMetadata> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      const metadata: ImageMetadata = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        fileSize: file.size,
        fileType: file.type,
      };
      
      // 清理资源
      URL.revokeObjectURL(url);
      resolve(metadata);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image metadata'));
    };
    
    img.src = url;
  });
}

/**
 * 校验图片分辨率
 * @param width 图片宽度
 * @param height 图片高度
 * @param options 分辨率限制选项
 * @returns 校验结果
 */
export function validateImageResolution(
  width: number,
  height: number,
  options: {
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): boolean {
  const { minWidth, minHeight, maxWidth, maxHeight } = options;
  
  if (minWidth !== undefined && width < minWidth) {
    return false;
  }
  
  if (minHeight !== undefined && height < minHeight) {
    return false;
  }
  
  if (maxWidth !== undefined && width > maxWidth) {
    return false;
  }
  
  if (maxHeight !== undefined && height > maxHeight) {
    return false;
  }
  
  return true;
}

/**
 * 校验文件大小
 * @param fileSize 文件大小（字节）
 * @param minSize 最小文件大小（字节）
 * @param maxSize 最大文件大小（字节）
 * @returns 校验结果
 */
export function validateFileSize(
  fileSize: number,
  minSize?: number,
  maxSize?: number
): boolean {
  if (minSize !== undefined && fileSize < minSize) {
    return false;
  }
  
  if (maxSize !== undefined && fileSize > maxSize) {
    return false;
  }
  
  return true;
}

/**
 * 校验文件类型
 * @param fileType 文件MIME类型
 * @param allowedTypes 允许的文件类型数组 为空则都允许
 * @returns 校验结果
 */
export function validateFileType(
  fileType: string,
  allowedTypes: string[] = [],
): boolean {
  return allowedTypes.length === 0 || allowedTypes.includes(fileType);
}

/**
 * 综合校验图片文件
 * @param file 图片文件
 * @param options 校验选项
 * @returns Promise<ValidationResult>
 */
export async function validateImageReason(
  file: File,
  options: ImageValidationOptions = {}
): Promise<ValidationResult> {
  
  try {
    // 首先进行文件类型校验（不需要加载图片元数据）
    if (options.allowedTypes) {
      const typeResult = validateFileType(file.type, options.allowedTypes);
      if (!typeResult) {
        return {
          isValid: false,
          errorType: ImageInvalid.FILE_TYPE,
          errorMessage: `File type ${file.type} is not allowed`
        };
      }
    }
    
    // 文件大小校验（不需要加载图片元数据）
    if (options.minFileSize !== undefined || options.maxFileSize !== undefined) {
      const sizeResult = validateFileSize(
        file.size,
        options.minFileSize,
        options.maxFileSize
      );
      
      if (!sizeResult) {
        console.log('File size out of range', { fileSize: file.size, minSize: options.minFileSize, maxSize: options.maxFileSize });
        return {
          isValid: false,
          errorType: ImageInvalid.FILE_SIZE,
          errorMessage: `File size ${file.size} bytes is out of range`
        };
      }
    }
    
    // 获取图片元数据进行进一步校验
    const metadata = await getImageMetadata(file);
    
    // 校验分辨率
    const resolutionOptions = {
      minWidth: options.minWidth,
      minHeight: options.minHeight,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
    };
    
    if (Object.values(resolutionOptions).some(val => val !== undefined)) {
      const resolutionResult = validateImageResolution(
        metadata.width,
        metadata.height,
        resolutionOptions
      );
      
      if (!resolutionResult) {
        console.log('Image resolution out of range', { width: metadata.width, height: metadata.height, minWidth: options.minWidth, minHeight: options.minHeight, maxWidth: options.maxWidth, maxHeight: options.maxHeight });
        return {
          isValid: false,
          errorType: ImageInvalid.RESOLUTION,
          errorMessage: `Image resolution ${metadata.width}x${metadata.height} is out of range`
        };
      }
    }
    
    return {
      isValid: true
    };
  } catch (error) {
    return {
      isValid: false,
      errorType: ImageInvalid.UNKNOWN,
      errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}
